"use strict";
/**
 * @license
 * Copyright 2019 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import "#src/ui/statistics.css";
import { debounce } from "lodash-es";
import {
  ChunkDownloadStatistics,
  ChunkMemoryStatistics,
  ChunkPriorityTier,
  ChunkState,
  getChunkDownloadStatisticIndex,
  getChunkStateStatisticIndex,
  numChunkMemoryStatistics,
  numChunkStates
} from "#src/chunk_manager/base.js";
import { SidePanel } from "#src/ui/side_panel.js";
import { TrackableSidePanelLocation } from "#src/ui/side_panel_location.js";
import { removeChildren } from "#src/util/dom.js";
import { emptyToUndefined } from "#src/util/json.js";
const DEFAULT_STATISTICS_PANEL_LOCATION = {
  side: "bottom",
  size: 100,
  minSize: 50,
  row: 0,
  col: 0,
  flex: 1,
  visible: false
};
export class StatisticsDisplayState {
  constructor() {
    this.location = new TrackableSidePanelLocation(DEFAULT_STATISTICS_PANEL_LOCATION);
  }
  get changed() {
    return this.location.changed;
  }
  restoreState(obj) {
    this.location.restoreState(obj);
  }
  reset() {
    this.location.reset();
  }
  toJSON() {
    return emptyToUndefined(this.location.toJSON());
  }
}
function getProperties(obj) {
  const map = /* @__PURE__ */ new Map();
  function handleObject(o, prefix) {
    if (o == null || typeof o !== "object") {
      map.set(prefix, "" + o);
      return;
    }
    for (const key of Object.keys(o)) {
      handleObject(o[key], prefix + "." + key);
    }
  }
  handleObject(obj, "");
  return map;
}
function getDistinguishingProperties(properties) {
  const selected = /* @__PURE__ */ new Set();
  selected.add(".type");
  const allProps = /* @__PURE__ */ new Set();
  function areDistinguished(i, j) {
    for (const prop of selected) {
      if (properties[i].get(prop) !== properties[j].get(prop)) {
        return true;
      }
    }
    return false;
  }
  for (let i = 0, n = properties.length; i < n; ++i) {
    for (const prop of properties[i].keys()) {
      allProps.add(prop);
    }
    let matches = [];
    for (let j = 0; j < i; ++j) {
      if (!areDistinguished(i, j)) {
        matches.push(j);
      }
    }
    while (matches.length > 0) {
      let bestReducedMatches = matches;
      let bestProp = void 0;
      for (const prop of allProps) {
        if (selected.has(prop)) continue;
        const reducedMatches = [];
        for (const j of matches) {
          if (properties[j].get(prop) === properties[i].get(prop)) {
            reducedMatches.push(j);
          }
        }
        if (reducedMatches.length < bestReducedMatches.length) {
          bestReducedMatches = reducedMatches;
          bestProp = prop;
        }
        if (reducedMatches.length === 0) break;
      }
      if (bestProp === void 0) break;
      matches = bestReducedMatches;
      selected.add(bestProp);
    }
  }
  return Array.from(selected);
}
function getNameFromProps(properties, selected) {
  const result = {};
  for (const prop of selected) {
    const value = properties.get(prop);
    if (value === void 0) continue;
    if (prop === "") return value;
    result[prop] = value;
  }
  return JSON.stringify(result);
}
export function getChunkSourceIdentifier(source) {
  return Object.assign({ type: source.RPC_TYPE_ID }, source.key || {});
}
export function getFormattedNames(objects) {
  const properties = objects.map(getProperties);
  const selectedProps = getDistinguishingProperties(properties);
  return properties.map((p) => getNameFromProps(p, selectedProps));
}
const requestDataInterval = 1e3;
export const columnSpecifications = [
  {
    label: "Visible chunks/T",
    key: "visibleChunksTotal",
    getter: (statistics) => {
      let sum = 0;
      for (let state = 0; state < numChunkStates; ++state) {
        sum += statistics[getChunkStateStatisticIndex(state, ChunkPriorityTier.VISIBLE) * numChunkMemoryStatistics + ChunkMemoryStatistics.numChunks];
      }
      return sum;
    }
  },
  {
    label: "Visible chunks/D",
    key: "visibleChunksDownloading",
    getter: (statistics) => {
      return statistics[getChunkStateStatisticIndex(
        ChunkState.DOWNLOADING,
        ChunkPriorityTier.VISIBLE
      ) * numChunkMemoryStatistics + ChunkMemoryStatistics.numChunks];
    }
  },
  {
    label: "Visible chunks/M",
    key: "visibleChunksSystemMemory",
    getter: (statistics) => {
      return statistics[getChunkStateStatisticIndex(
        ChunkState.SYSTEM_MEMORY,
        ChunkPriorityTier.VISIBLE
      ) * numChunkMemoryStatistics + ChunkMemoryStatistics.numChunks] + statistics[getChunkStateStatisticIndex(
        ChunkState.SYSTEM_MEMORY_WORKER,
        ChunkPriorityTier.VISIBLE
      ) * numChunkMemoryStatistics + ChunkMemoryStatistics.numChunks];
    }
  },
  {
    label: "Visible chunks/G",
    key: "visibleChunksGpuMemory",
    getter: (statistics) => {
      return statistics[getChunkStateStatisticIndex(
        ChunkState.GPU_MEMORY,
        ChunkPriorityTier.VISIBLE
      ) * numChunkMemoryStatistics + ChunkMemoryStatistics.numChunks];
    }
  },
  {
    label: "Visible chunks/F",
    key: "visibleChunksFailed",
    getter: (statistics) => {
      return statistics[getChunkStateStatisticIndex(
        ChunkState.FAILED,
        ChunkPriorityTier.VISIBLE
      ) * numChunkMemoryStatistics + ChunkMemoryStatistics.numChunks];
    }
  },
  {
    label: "Visible memory",
    key: "visibleGpuMemory",
    getter: (statistics) => {
      return statistics[getChunkStateStatisticIndex(
        ChunkState.GPU_MEMORY,
        ChunkPriorityTier.VISIBLE
      ) * numChunkMemoryStatistics + ChunkMemoryStatistics.gpuMemoryBytes];
    }
  },
  {
    label: "Download latency",
    key: "downloadLatency",
    getter: (statistics) => {
      return statistics[getChunkDownloadStatisticIndex(ChunkDownloadStatistics.totalTime)] / statistics[getChunkDownloadStatisticIndex(ChunkDownloadStatistics.totalChunks)];
    }
  }
];
export class StatisticsPanel extends SidePanel {
  constructor(sidePanelManager, chunkQueueManager, displayState) {
    super(sidePanelManager, displayState.location);
    this.chunkQueueManager = chunkQueueManager;
    this.displayState = displayState;
    this.data = void 0;
    this.requestDataTimerId = -1;
    this.dataRequested = false;
    this.body = document.createElement("div");
    this.debouncedUpdateView = this.registerCancellable(
      debounce(() => this.updateView(), 0)
    );
    const { body } = this;
    body.classList.add("neuroglancer-statistics-panel-body");
    this.addTitleBar({ title: "Chunk statistics" });
    this.addBody(body);
    this.requestData();
  }
  disposed() {
    window.clearTimeout(this.requestDataTimerId);
    super.disposed();
  }
  requestData() {
    if (this.dataRequested) return;
    const { chunkQueueManager } = this;
    this.dataRequested = true;
    chunkQueueManager.getStatistics().then((data) => {
      this.dataRequested = false;
      this.data = data;
      this.debouncedUpdateView();
      this.requestDataTimerId = window.setTimeout(() => {
        this.requestDataTimerId = -1;
        this.requestData();
      }, requestDataInterval);
    });
  }
  updateView() {
    const { data } = this;
    if (data === void 0) return;
    const table = document.createElement("table");
    const rows = [];
    for (const [source, statistics] of data) {
      const row = [source];
      for (const { getter } of columnSpecifications) {
        row.push(getter(statistics));
      }
      rows.push(row);
    }
    const formattedNames = getFormattedNames(
      rows.map((x) => getChunkSourceIdentifier(x[0]))
    );
    const sourceFormattedNames = /* @__PURE__ */ new Map();
    formattedNames.forEach((name, i) => {
      sourceFormattedNames.set(rows[i][0], name);
    });
    {
      const thead = document.createElement("thead");
      let tr = document.createElement("tr");
      thead.appendChild(tr);
      const addHeaderColumn = (label) => {
        const td = document.createElement("td");
        td.textContent = label;
        tr.appendChild(td);
      };
      addHeaderColumn("Name");
      let prevPrefix = void 0;
      for (const { label: column } of columnSpecifications) {
        const sepIndex = column.indexOf("/");
        let prefix = column;
        if (sepIndex !== -1) {
          prefix = column.substring(0, sepIndex);
          if (prefix === prevPrefix) {
            ++tr.lastElementChild.colSpan;
            continue;
          }
          prevPrefix = prefix;
        }
        addHeaderColumn(prefix);
      }
      tr = document.createElement("tr");
      thead.appendChild(tr);
      {
        const td = document.createElement("td");
        tr.appendChild(td);
      }
      for (const { label: column } of columnSpecifications) {
        const sepIndex = column.indexOf("/");
        let suffix = "";
        if (sepIndex !== -1) {
          suffix = column.substring(sepIndex + 1);
        }
        const td = document.createElement("td");
        td.textContent = suffix;
        tr.appendChild(td);
      }
      table.appendChild(thead);
    }
    const tbody = document.createElement("tbody");
    for (const [source, ...values] of rows) {
      const tr = document.createElement("tr");
      const addColumn = (label) => {
        const td = document.createElement("td");
        td.textContent = label;
        tr.appendChild(td);
      };
      addColumn(sourceFormattedNames.get(source));
      for (const value of values) {
        addColumn("" + value);
      }
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    removeChildren(this.body);
    this.body.appendChild(table);
  }
}
